import { DialogTitle, Icon } from '@aibees/shared/components';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { useDeletePaymentCardMutationApi } from '../../api/use-delete-payment-card-mutation-api';
import { PaymentCardInfo } from '../payment-card-info';
import * as Styled from './styles';
import * as SX from './sx';
import type { RemovePaymentCardProps as Props } from './types';

export const RemovePaymentCard = memo(({ className, item }: Props) => {
  const notify = useNotify();
  const dialog = useBoolean();
  const loading = useBoolean();

  const api = useDeletePaymentCardMutationApi(item.id);

  const handleClose = () => {
    dialog.onFalse();
  };

  const handleRemove = () => {
    loading.onTrue();
    api.mutateAsync({}).then(() => {
      notify.success('Card Successfully removed');
      loading.onFalse();
      handleClose();
    });
  };

  return (
    <>
      <Box
        className={classnames('RemovePaymentCard-trigger', className)}
        sx={SX.box}
        onClick={dialog.onToggle}
      >
        <Icon name="delete" color="error" size={18} />
      </Box>

      <Styled.Wrapper
        className={classnames('RemovePaymentCard', className)}
        open={dialog.value}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle title="Delete Card" icon="delete" onClose={handleClose} />

        <DialogContent>
          <Card variant="outlined">
            <CardContent padding="extra">
              <Stack gap={1.5}>
                <Typography variant="body2" fontWeight={700} color="grey.700">
                  Are you sure you want to delete this card?
                </Typography>
                <PaymentCardInfo item={item.card} />
              </Stack>
            </CardContent>
          </Card>
        </DialogContent>

        <DialogActions>
          <Button variant="text" size="small" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            size="small"
            loading={loading.value || api.isLoading}
            onClick={handleRemove}
          >
            Yes, Delete
          </LoadingButton>
        </DialogActions>
      </Styled.Wrapper>
    </>
  );
});

RemovePaymentCard.displayName = 'RemovePaymentCard';
